import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Layers,
  Image,
  Eraser,
    Building2,
    HardHat,
    PackagePlus,
    Film,
    Gamepad2,
    Megaphone,
    CalendarDays,
    Search,
    Play,
    Palette,
    Ruler,
    Zap,
    Eye,
    ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ImageCompareSlider from '../components/ImageCompareSlider';
import Embedded3DViewer from '../components/Embedded3DViewer';
import BackgroundVideo from '../components/BackgroundVideo';
import { Monitor, Globe, Terminal } from 'lucide-react';
import ReconstructionAgent from '../components/ReconstructionAgent';
import SiteFooter from '../components/SiteFooter';
import SiteHeader from '../components/SiteHeader';

// --- Cinematic Applications Component ---
/* Mirrors SURFACES on /technology — same destinations, same order as the nav.
   Icons in ember, as on the four product pages: gold marks a labelled item,
   crane is reserved for things you click. */
const SURFACES = [
    { to: '/desktop', Icon: Monitor,  label: 'Cr3ate Desktop',     line: 'Reconstruct on your own machine, with nothing leaving it.', cta: 'Get Desktop App' },
    { to: '/reco',    Icon: Globe,    label: 'Images to 3D',       line: 'Give us your images and get a 3D model back.',              cta: 'Try Images to 3D' },
    { to: '/sdk',     Icon: Terminal, label: 'Reconstruction SDK', line: 'Call the pipeline stage by stage from your own code.',      cta: 'Get the SDK' },
] as const;

const CinematicApplications = () => {
    const [active, setActive] = useState(0);
    const applications = [
        { icon: Building2, title: 'Architecture', description: 'Turn site photos into accurate 3D models to visualize and iterate designs in real context before building.', gradient: 'from-sky-500/40 to-indigo-500/40', image: '/Architecture.jpeg' },
        { icon: HardHat, title: 'Engineering & Construction', description: 'Document job sites in 3D with just a phone, track changes over time, and improve collaboration via shareable links.', gradient: 'from-amber-500/40 to-orange-600/40', image: '/Construction.jpeg' },
        { icon: Building2, title: 'Real Estate', description: 'Capture any space in stunning 3D straight from your phone, and share it as an interactive virtual tour.', gradient: 'from-emerald-500/40 to-green-600/40', image: '/Realestate.jpeg' },
        { icon: PackagePlus, title: 'Product Design and Showcase', description: 'Bring your products to life in 3D—perfect for showcasing and speeding up iteration.', gradient: 'from-teal-500/40 to-cyan-600/40', image: '/product.jpeg' },
        { icon: Film, title: 'Film & TV', description: 'Capture real locations and convert them into digital sets or VFX-ready scenes with a single camera pass', gradient: 'from-purple-500/40 to-violet-600/40', image: '/VFX.jpeg'},
        { icon: Gamepad2, title: 'Gaming & LBE', description: 'Scan physical environments to create authentic worlds with photorealistic textures and geometry.', gradient: 'from-slate-500/40 to-grey-600/40', image: '/game.jpeg' },
        { icon: CalendarDays, title: 'Events', description: 'Digitize venues in 3D with just your phone, then plan and compare layouts against the real space.', gradient: 'from-emerald-500/40 to-green-600/40', image: '/Events.jpeg' },
        { icon: Search, title: 'Forensics', description: 'Capture a scene, object or individual in 3D from a phone, preserved to scale and revisitable long after the site is released.', gradient: 'from-red-500/40 to-rose-600/40', image: '/Forensics.jpeg'},
    ];

    return (
        <div className="w-full max-w-7xl mx-auto flex flex-col items-center overflow-hidden">
            {/* Mobile/Tablet: Background Image with Text Overlay */}
            <div className="md:hidden w-full space-y-3 px-4 max-w-full overflow-hidden">
                {applications.map((app, i) => (
<div
  key={app.title}
  className={`relative rounded-xl overflow-hidden transition-all duration-500 max-w-full ${
  active === i ? 'min-h-[380px]' : 'min-h-[80px]'
} md:min-h-0`}
  style={{
    backgroundImage: `url(${app.image})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  }} 
>
                        {/* Background overlays */}
                        <div className={`absolute inset-0 bg-gradient-to-t ${app.gradient} mix-blend-hard-light`} />
                        <div className="absolute inset-0 bg-black/50" />
                        
                        {/* Content */}
                        <div className="relative z-10">
                            <button
                                className="w-full p-4 flex items-center justify-between text-left focus:outline-none"
                                onClick={() => setActive(active === i ? -1 : i)}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-white/20 backdrop-blur-md border border-white/30 rounded-xl flex items-center justify-center">
                                        <app.icon className="w-5 h-5 text-ink-100" />
                                    </div>
                                    <h3 className="text-title font-semibold text-ink-100 uppercase tracking-wide [text-shadow:0_2px_4px_rgba(0,0,0,0.8)]">
                                        {app.title}
                                    </h3>
                                </div>
                                <div className={`transform transition-transform duration-300 text-ink-100 ${active === i ? 'rotate-180' : ''}`}>
                                    ▼
                                </div>
                            </button>
                            {active === i && (
                                <div className="px-4 pb-4">
                                    <p className="text-ink-100 text-ui leading-relaxed [text-shadow:0_1px_3px_rgba(0,0,0,0.8)]">
                                        {app.description}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop: Original Slider Layout from provided document */}
            <div className="hidden md:flex w-full h-[70vh] min-h-[600px] gap-2 overflow-hidden max-w-full">
                {applications.map((app, i) => (
                    <div
                        key={app.title}
                        className={`transition-all duration-700 ease-in-out overflow-hidden relative rounded-2xl cursor-pointer shadow-2xl flex flex-col ${i === active ? 'flex-[3]' : 'flex-[1]'}`}
                        onMouseEnter={() => setActive(i)} 
                    >
                        <img src={app.image} alt={app.title} className="absolute inset-0 w-full h-full object-cover" />
                        <div className={`absolute inset-0 bg-gradient-to-t ${app.gradient} mix-blend-hard-light`} />
                        <div className="absolute inset-0 bg-black/40" />
                        <div className="relative z-10 w-full h-full px-4 py-5 flex flex-col justify-between text-ink-100">
                            <div className={`flex flex-col ${i === active ? 'items-start' : 'items-start'} pl-2`}>
                                <div className="w-10 h-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center mb-4 translate-x-[10px]">
                                    <app.icon className="w-5 h-5 text-ink-100" />
                                </div>
                                <h3 className="[writing-mode:vertical-rl] rotate-180 text-title font-semibold uppercase tracking-wide [text-shadow:0_1px_2px_rgba(0,0,0,0.5)] ml-[14px]">
                                    {app.title}
                                </h3>
                            </div>
                            {i === active && (
                                <div className="mt-auto">
                                    <p className="text-title opacity-90 [text-shadow:0_1px_3px_rgba(0,0,0,0.5)]">
                                        {app.description}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop: Original dot navigation from provided document */}
            <div className="hidden md:flex justify-center gap-3 mt-6">
                {applications.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setActive(i)}
                        className={`transition-all duration-300 rounded-full ${i === active ? 'w-4 h-2 bg-white' : 'w-2 h-2 bg-gray-500 hover:bg-gray-300'}`}
                        aria-label={`Go to slide ${i + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};
 
// --- Workflow Slider Component --- 
const WorkflowSlider = () => {
  const slides = [
    {
      step: 1,
      title: 'Capture',
      heading: 'Take photos with any camera',
      subtext:
        'From small objects to entire cities — we support any device and scale you shoot day or night. ',
      image: '/Step1.jpeg',
    },
    {
      step: 2,
      title: 'Upload',
      heading: 'Fully automated 3D reconstruction in less than 24 hours',
      subtext:
        'Powered by cutting-edge Gaussian Splats. 3D models delivered in hours, not days or weeks.',
      image: '/Step2.jpeg',
    },
    {
      step: 3,
      title: 'Share',
      heading: 'Instant web viewer — no download needed',
      subtext:
        'Publish your immersive scene with a simple link anyone can view',
      image: '/Step3.jpeg',
    },
  ];

  const [active, setActive] = useState(0);
 
  return (
   <div className="w-full flex flex-col sm:flex-row h-[70vh] sm:h-[50vh]">
      {slides.map((slide, i) => (
        <div
          key={slide.step}
          className={`relative transition-all duration-700 ease-in-out overflow-hidden cursor-pointer ${
            i === active ? 'flex-[3]' : 'flex-[1] bg-black/90'
          }`} 
          onMouseEnter={() => setActive(i)}
          onClick={() => setActive(i)}
        >
          {/* Background image */}
          <img
            src={slide.image}
            alt={slide.title}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
              i === active ? 'opacity-100' : 'opacity-10'
            }`} 
          />

          {/* Unified container for the entire overlay content */}
          <div className="absolute bottom-6 left-4 right-4 sm:left-6 sm:right-6 z-20 pointer-events-none">
            {/* Flex container to align the step circle and the text content */}
            <div className="flex items-start gap-3 bg-black/70 backdrop-blur-md rounded-lg p-4 sm:p-6">
              {/* Step circle */}
              <div className="w-6 h-6 sm:w-7 sm:h-7 bg-white/10 border border-white/30 rounded-full flex items-center justify-center text-ui font-semibold text-ink-100 flex-shrink-0">
                {slide.step}
              </div>

              {/* All text content is now in this single flex column */}
              <div className="flex flex-col">
                {/* Title */}
                <span className="text-ui uppercase font-medium text-ink-100/70 tracking-wide">
                  {slide.title}
                </span>

                {/* Heading and subtext, conditionally shown with consistent alignment */}
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    i === active ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden space-y-1 pt-1">
                    <h2 className="text-body sm:text-title font-semibold text-ink-100 leading-tight">
                      {slide.heading}
                    </h2>
                    <p className="text-ui text-ink-100/80 leading-snug">
                      {slide.subtext}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div> 
        </div>
      ))}
    </div>
  );
}; 

// --- Main Page Component ---
const MarketingPage: React.FC = () => {
    const navigate = useNavigate();
    const { authenticated } = useAuth();
    const [show3DViewer, setShow3DViewer] = useState(false);
    
    // Quote expansion state for mobile
    const [isQuoteExpanded, setIsQuoteExpanded] = useState(false);
    const [displayQuote, setDisplayQuote] = useState('');
    const [showToggle, setShowToggle] = useState(false);
    
    // Full quote text
    const fullQuoteText = "Cr3ate.ai quality has consistently exceeded our expectations, setting a new standard for how we capture and reconstruct environments for post-production. At Pixomondo, we have it integrated into various stages of our production pipeline. During pre-production, it has been invaluable for scouting, tech-visualization, and pre-visualization. During production, we've used it to capture environments and reconstruct them for in-camera VFX and car processing on LED Volumes. In post-production, we've seamlessly imported these captured environments into Unreal Engine, where we've been able to enhance and expand upon them.";

    // Handle quote truncation for mobile
    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth < 768; // md breakpoint
            
            if (isMobile) {
                if (!isQuoteExpanded) {
                    // Show approximately 50% of the quote
                    const words = fullQuoteText.split(' ');
                    const halfLength = Math.floor(words.length / 3);
                    const truncatedText = words.slice(0, halfLength).join(' ') + '...';
                    setDisplayQuote(truncatedText);
                } else {
                    setDisplayQuote(fullQuoteText);
                }
                setShowToggle(true);
            } else {
                // Desktop - always show full quote
                setDisplayQuote(fullQuoteText);
                setShowToggle(false);
            }
        };
        
        // Call once on mount
        handleResize();
        
        // Listen for resize events
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isQuoteExpanded, fullQuoteText]);
    
    useEffect(() => {
        if (authenticated) {
            navigate('/dashboard');
        }
    }, [authenticated, navigate]);


    /*
     * font-brand once on the root rather than per element: this page was on the
     * Tailwind default sans while every other public page used the brand stack,
     * which is the largest single reason they did not look related. Children
     * that set their own font still win.
     */
    return (
        <div className="min-h-screen bg-ink-950 font-brand">
 <SiteHeader />





            <main className="overflow-x-hidden max-w-full">
                {/* --- HERO SECTION --- */}
                {/* Full viewport, not 90vh. The header is sticky and sits in flow, so a
                    90vh hero can never reach the fold — it ends a header's height
                    short and shows the next section underneath. That gap only became
                    visible when SiteHeader replaced the old, taller header. */}
                <section className="relative min-h-screen flex items-center overflow-hidden max-w-full">
                   {/* 🔹 Overlay Tip */}
                  <div className="absolute bottom-4 right-4 z-10 text-ink-100 text-ui px-3 py-1.5 bg-black/20 rounded-md backdrop-blur-sm pointer-events-none">
                    High-fidelity 3D reconstruction of a Vancouver, Canada street captured by Pixomondo
                  </div>
                    {/* Background Video + Effects */}
                    <div className="absolute inset-0 z-0 overflow-hidden">
                        <BackgroundVideo
                            videoSrc="https://med.cr3ate.ai/cr3ate_hero_bg.mp4"
                            stillSrc="https://med.cr3ate.ai/cr3ate_hero_image.jpg"
                        />
                        <div className="absolute top-1/3 -right-20 w-80 h-80 bg-crane-500 opacity-10 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-crane-700 opacity-10 rounded-full blur-3xl"></div>
                    </div>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative z-10">
                        <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
                            <h1
                                className="text-3xl sm:text-5xl lg:text-6xl font-bold text-ink-100 leading-tight mb-4 tracking-tight"
                                style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}
                            >
                                {/* Each half is unbreakable, so the line either
                                    sits whole or splits once at the comma — it
                                    never strands "Create" or "Intelligence"
                                    alone on a second line. */}
                                <span className="whitespace-nowrap">Capture Reality,</span>{' '}
                                <span className="whitespace-nowrap">Create Intelligence</span>
                            </h1>

                            <h2
                                className="text-body sm:text-2xl font-bold text-crane-500 mb-8 tracking-wide"
                                style={{ textShadow: '0 1px 5px rgba(0,0,0,0.5)' }}
                            >
                                One platform for 3D reconstruction and spatial intelligence
                            </h2>

                            {/* Mobile version of paragraph */}
                            <p
                                className="block sm:hidden text-body text-ink-200 mb-10 max-w-sm px-4 leading-relaxed"
                                style={{ textShadow: '0 1px 5px rgba(0,0,0,0.5)' }}
                            >
                                Get a head start with effortless 3D reconstructions
                            </p>

                            {/* Desktop version of paragraph */} 
                            <p
                                className="hidden sm:block text-2xl text-ink-200 mb-12 max-w-3xl px-0 leading-normal" 
                                style={{ textShadow: '0 1px 5px rgba(0,0,0,0.5)' }}
                            >
                                Get a head start with effortless 3D reconstructions
                            </p>

                            {/* A two-up grid rather than a flex row: `grid-cols-2` gives both
                                columns the width of the wider label, so the buttons match
                                without either being pinned to a hardcoded width that the
                                next copy change would break. One column on a phone. */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 w-full sm:w-auto px-4 sm:px-0">
                                <Link
                                    to="/desktop"
                                    className="px-6 py-2 sm:px-7 sm:py-2.5 bg-gradient-to-r from-crane-500 to-crane-500/80 text-ink-900 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all text-body sm:text-title hover:scale-105 text-center"
                                >
                                    Get Desktop App
                                </Link>
                                <Link
                                    to="/reco"
                                    className="px-6 py-2 sm:px-7 sm:py-2.5 bg-gradient-to-r from-crane-500 to-crane-500/80 text-ink-900 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all text-body sm:text-title hover:scale-105 text-center"
                                >
                                    Try Images to 3D
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
           
                {/* --- IMAGE COMPARE SECTION --- */}
                {/* The one colour here that is not a palette value: a blue-tinted ground
                    that separates the comparison from the hero above it. Kept as a hex
                    deliberately — folding it into ink-950 would flatten that boundary,
                    and naming it in the palette for one use is not worth the entry. */}
                <section className="relative bg-[#0B1621] overflow-hidden pt-32 pb-16 max-w-full">
                    <div className="relative z-10 max-w-7xl mx-auto px-6">
                        <h2 className="text-ink-100 text-2xl sm:text-4xl font-bold text-center mb-4 leading-tight text-balance">Studio Grade Quality + Survey Grade Accuracy</h2>
                        <p className="text-center text-ink-300 mb-10">Our reconstructions outperform others with outstanding metric accuracy and Gaussian splatting quality. Drag the slider to experience the difference.</p>
                        <div className="bg-slate-800/60 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/10 p-4 md:p-8 max-w-full overflow-hidden" style={{boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.05)'}}>
                            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center">
                                <div className="w-full md:w-3/5">
                                    <ImageCompareSlider
                                        beforeImageSrc="/Teleport-foliage.png"
                                        afterImageSrc="/Ydrive-foliage.png"
                                        beforeLabel="Other reconstruction pipelines"
                                        afterLabel="CR3ATE.AI"
                                    />
                                </div>
                                <div className="w-full md:w-2/5">
                                    <div className="space-y-6">
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <Layers className="w-5 h-5 text-ink-100" />
                                            </div>
                                            <div>
                                                <h3 className="text-title font-bold text-ink-100">Lifelike Detail</h3>
                                                <p className="text-ink-300 leading-relaxed text-ui mt-1">
                                                    True-to-life, crisp textures, and natural lighting.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <Building2 className="w-5 h-5 text-ink-100" />
                                            </div>
                                            <div>
                                                <h3 className="text-title font-bold text-ink-100">Built for Scale</h3>
                                                <p className="text-ink-300 leading-relaxed text-ui mt-1">
                                                    Others stop at 10K images, we don't. From objects to cities, we scale with you.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <Palette className="w-5 h-5 text-ink-100" />
                                            </div>
                                            <div>
                                                <h3 className="text-title font-bold text-ink-100">True Color</h3>
                                                <p className="text-ink-300 leading-relaxed text-ui mt-1">
                                                    Stunningly vibrant colors and realistic shadows for an immersive experience.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* New Image Comparison Slider with updated highlights */}
                      <div className="bg-slate-800/60 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/10 p-4 md:p-8 mt-10 max-w-full overflow-hidden" style={{boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.05)'}}>
                            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center">
                                <div className="w-full md:w-3/5">
                                    <ImageCompareSlider
                                        beforeImageSrc="/Before.jpeg"
                                        afterImageSrc="/After.jpeg"
                                        beforeLabel="Our 3D Model"
                                        afterLabel="Our Normal Map"
                                        objectFit="object-cover"
                                        objectPosition="top left"
                                    /> 
                                </div>
                                <div className="w-full md:w-2/5">
                                    <div className="space-y-6">
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <Eraser className="w-5 h-5 text-ink-100" />
                                            </div>
                                           <div>
                                                <h3 className="text-title font-bold text-ink-100">Auto removal of Dynamics & Background</h3>
                                                <p className="text-ink-300 leading-relaxed text-ui mt-1">
                                                  Our AI removes people, cars, and objects in motion—saving time and cutting location shooting costs
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <Ruler className="w-5 h-5 text-ink-100" />
                                            </div>
                                             <div>
                                                <h3 className="text-title font-bold text-ink-100">Precise 3D Geometry</h3>
                                                <p className="text-ink-300 leading-relaxed text-ui mt-1">
                  High quality 3D structure & dimensions giving you a solid foundation for editing and design.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <Eye className="w-5 h-5 text-ink-100" />
                                            </div>
                                            <div>
                                                <h3 className="text-title font-bold text-ink-100">Online Normal Map Viewer</h3>
                                                <p className="text-ink-300 leading-relaxed text-ui mt-1">
                                                    Analyze the underlying 3D structure and accuracy via our online viewer.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                        </div>
                    </div>
                </section>
                
             


          
{/* --- PIXO Section---*/} 
<section className="relative w-full min-h-screen bg-black mt-24 sm:mt-32 overflow-hidden">
  <div className="absolute inset-0 z-0 overflow-hidden">
    <BackgroundVideo
      videoSrc="https://med.cr3ate.ai/cr3ate_customer_CES.mp4"
      stillSrc="https://med.cr3ate.ai/cr3ate_customer_CES.jpg"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/50 to-black/80" />
  </div>

  <div className="relative z-10 pt-10 pb-20 px-6 min-h-screen flex flex-col justify-between">
    <div className="text-center">
      <h2 className="text-ink-100 text-2xl sm:text-4xl font-bold text-center mb-4">
        Trusted by industry leaders
      </h2>
      {/* Sits at the top of the section, where the scrim above is at its
          weakest (black/20 against the card's black/80 at the foot). Lifted
          off ink-300 and given the same shadow the hero uses over video, so it
          holds up against the brighter frames rather than only the dark ones. */}
      <p
        className="text-center text-ink-200 max-w-5xl mx-auto"
        style={{ textShadow: '0 1px 6px rgba(0,0,0,0.75)' }}
      >
        At CES 2025, our 3D reconstruction of Vancouver streets took center stage rendering live on Pixomondo's LED Volume — redefining in-camera VFX for virtual production.
      </p>
    </div>
    
    {/* Testimonial + Button wrapper */}
    <div className="flex flex-col items-center justify-center px-4 mb-0 sm:mb-8 gap-8">

      {/* Testimonial Card */}
      <div className="backdrop-blur-lg bg-white/10 border border-white/10 rounded-xl p-4 md:p-6 max-w-7xl w-full text-ink-100 shadow-xl flex flex-col md:flex-row items-center gap-4 md:gap-10">
        <div className="flex flex-col items-center text-center flex-shrink-0">
          <img
            src="/Mahmoud.jpeg"
            alt="Mahmoud Rahnama"
            className="w-16 h-16 md:w-24 md:h-24 rounded-full object-cover border border-white/20 shadow-md mb-2"
          />
          <div className="text-ui">
            <p className="font-medium">Mahmoud Rahnama</p>
            <p className="text-crane-500 font-normal">Chief Innovation Officer, Pixomondo</p>
          </div>
        </div>
        <div className="flex-1 text-left">
          <blockquote className="text-ui md:text-body italic leading-relaxed font-normal">
            "{displayQuote}"
          </blockquote>
          {showToggle && (
            <button
              onClick={() => setIsQuoteExpanded(!isQuoteExpanded)}
              className="mt-2 text-ui text-crane-500 hover:text-crane-500/80 transition-colors font-medium"
            >
              {isQuoteExpanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      </div>
      

    </div>
  </div>
</section>
{/* --- PIXO section ends --- */}



                 
               {/* --- THREE WAYS SECTION ---
                    The page had no route to any product page: the nav was the only
                    way to learn they exist. Same three destinations /technology
                    offers, in the same order the nav now uses, so a reader meets
                    them twice in the same sequence. */}
                <section className="relative overflow-hidden bg-[#0B1621] py-24 sm:py-32">
                    <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6">
                        <h2 className="mb-4 text-center text-3xl font-bold text-ink-100 sm:text-4xl lg:text-5xl">Three ways to put Cr3ate to work right away</h2>
                        <p className="mx-auto mb-12 max-w-3xl text-center text-body text-ink-300 sm:text-title">
                            Desktop, Online, SDK — your scenario, your data, your choice.
                        </p>

                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 sm:gap-6">
                            {SURFACES.map(({ to, Icon, label, line, cta }) => (
                                <div
                                    key={to}
                                    className="flex flex-col rounded-2xl border border-ink-800 bg-ink-950/70 p-7 transition-colors hover:border-crane-500/50 sm:p-8"
                                >
                                    <div className="flex items-center gap-3">
                                        <Icon className="h-6 w-6 flex-shrink-0 text-ember-500" aria-hidden />
                                        <h3 className="font-brand text-xl font-medium tracking-wide text-ink-100">{label}</h3>
                                    </div>
                                    <p className="mt-3 flex-1 text-body font-light leading-relaxed text-ink-300">{line}</p>
                                    <Link
                                        to={to}
                                        className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-crane-500 px-4 py-3 font-brand text-body font-medium text-ink-950 transition-colors hover:bg-crane-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950"
                                    >
                                        {cta}
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

               {/* --- INDUSTRIES SECTION --- */}
                <section className="py-24 bg-ink-950 relative overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-ink-900/30 to-transparent z-0"></div>
                    <div className="relative z-10 px-4">
                        <div className="text-center mb-10">
                            <h2 className="text-2xl sm:text-4xl font-bold text-ink-100 text-center mb-4">One platform for any spatial industry</h2>
                            <p className="text-ink-300 max-w-3xl mx-auto">Wherever a place has to be measured, understood or shown, Cr3ate can help.</p>
                        </div>
                        <CinematicApplications />
                    </div>
                </section>


                
            </main>

            {/* --- 3D VIEWER MODAL --- */}
            <Embedded3DViewer 
                isOpen={show3DViewer} 
                onClose={() => setShow3DViewer(false)}
            />

            {/* --- RECONSTRUCTION AGENT — floating bottom-right overlay --- */}
            <ReconstructionAgent />
     {/* --- FOOTER --- */}
            <SiteFooter />

               </div>
    );
 };
export default MarketingPage;
